import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert, Button } from '@mui/material';

const SnackbarContext = createContext();

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within SnackbarProvider');
  }
  return context;
};

export const SnackbarProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info', // 'success' | 'error' | 'warning' | 'info'
    duration: 3000,
    actionLabel: '',
    onAction: null
  });

  const showSnackbar = useCallback((message, severity = 'info', options = {}) => {
    const normalizedOptions = typeof options === 'number' ? { duration: options } : options;
    setSnackbar({
      open: true,
      message,
      severity,
      duration: normalizedOptions.duration || 4000,
      actionLabel: normalizedOptions.actionLabel || '',
      onAction: normalizedOptions.onAction || null
    });
  }, []);

  const showSuccess = useCallback((message, options) => showSnackbar(message, 'success', options), [showSnackbar]);
  const showError = useCallback((message, options) => showSnackbar(message, 'error', options), [showSnackbar]);
  const showWarning = useCallback((message, options) => showSnackbar(message, 'warning', options), [showSnackbar]);
  const showInfo = useCallback((message, options) => showSnackbar(message, 'info', options), [showSnackbar]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleAction = () => {
    const action = snackbar.onAction;
    setSnackbar(prev => ({ ...prev, open: false }));
    action?.();
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar, showSuccess, showError, showWarning, showInfo }}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleClose}
          severity={snackbar.severity}
          variant="filled"
          action={snackbar.actionLabel ? (
            <Button color="inherit" size="small" onClick={handleAction} sx={{ minHeight: 44 }}>
              {snackbar.actionLabel}
            </Button>
          ) : undefined}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};
